import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Play, CheckCircle, Star, TrendingUp, Zap, Globe, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SalesPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(0);
  const [loading, setLoading] = useState(false);

  const testimonials = [
    {
      name: "Maria Silva",
      location: "São Paulo, SP",
      earnings: "R$ 8.500/mês",
      timeframe: "45 dias",
      text: "Em 45 dias já estava faturando R$ 8.500/mês sem fazer nada. O sistema roda sozinho enquanto eu durmo!",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b479?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "João Roberto",
      location: "Rio de Janeiro, RJ",
      earnings: "R$ 35.000/mês",
      timeframe: "3 meses",
      text: "Saí do meu emprego depois de 3 meses usando o sistema. Hoje faturo mais de R$ 35k/mês no piloto automático.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "Ana Costa",
      location: "Belo Horizonte, MG",
      earnings: "R$ 127.000",
      timeframe: "8 meses",
      text: "Nunca acreditei em 'dinheiro fácil' até conhecer este sistema. R$ 127.000 em 8 meses... Mudou minha vida!",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
    }
  ];

  const features = [
    {
      icon: <Zap className="h-6 w-6 text-yellow-500" />,
      title: "IA GPT-5 Integrada",
      description: "Gera conteúdo profissional 24/7 automaticamente"
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-green-500" />,
      title: "9 Plataformas Simultâneas",
      description: "ClickBank, Hotmart, Monetizze, Amazon, Shopee, Magalu, Mercado Livre, Awin e TikTok Shop"
    },
    {
      icon: <Globe className="h-6 w-6 text-blue-500" />,
      title: "Marketing Internacional",
      description: "Opera em 6 países ao mesmo tempo"
    },
    {
      icon: <DollarSign className="h-6 w-6 text-green-600" />,
      title: "Pagamentos Automáticos",
      description: "PIX, Stripe e PayPal processam sozinhos"
    }
  ];

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Capturar lead
      await axios.post(`${API}/capture-lead`, {
        email: email,
        source: 'sales_page',
        interest: 'garantir_acesso'
      });
      
      toast.success('Email cadastrado! Redirecionando para criar sua conta...');
      
      // Redirecionar para página de cadastro
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error) {
      console.error('Error capturing lead:', error);
      // Mesmo com erro, redirecionar
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleStartNow = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="text-2xl font-bold text-white">
              💰 <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">MonetizeMaster</span>
            </div>
            <button 
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-700 transition-all"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1920&h=1080&fit=crop')] bg-cover bg-center opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-2 rounded-full text-sm font-bold mb-6">
              🚨 OPORTUNIDADE LIMITADA - ÚLTIMAS 7 VAGAS
            </span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              SISTEMA REVOLUCIONÁRIO
            </span>
            <br />
            <span className="text-white">GERA R$ 15.000+ POR MÊS</span>
            <br />
            <span className="text-2xl md:text-4xl text-gray-300">NO PILOTO AUTOMÁTICO</span>
          </h1>
          
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            A Primeira Plataforma que Une <strong className="text-yellow-400">IA Multilíngue + 9 Plataformas de Afiliados + Alcance Global + Automação Total</strong>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button 
              onClick={() => setShowVideoModal(true)}
              className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-red-700 hover:to-red-800 transition-all flex items-center gap-2 shadow-2xl"
            >
              <Play className="h-6 w-6" />
              VER DEMONSTRAÇÃO GRÁTIS
            </button>
            <button 
              onClick={handleStartNow}
              className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-yellow-600 hover:to-orange-700 transition-all shadow-2xl"
            >
              QUERO COMEÇAR AGORA →
            </button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-400">2.847+</div>
              <div className="text-gray-400">Usuários Ativos</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">R$ 15.7M</div>
              <div className="text-gray-400">Em Comissões</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400">847</div>
              <div className="text-gray-400">Faturando R$10k+/mês</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-400">98.7%</div>
              <div className="text-gray-400">Satisfação</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-20 bg-black/20 backdrop-blur-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="text-red-400">PARE DE PERDER DINHEIRO</span> COM ESTES PROBLEMAS:
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-400 mb-3">❌ "Não sei por onde começar no marketing de afiliados"</h3>
              <p className="text-gray-300">Perda de tempo, oportunidades perdidas, análise paralisia</p>
            </div>
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-400 mb-3">✅ Sistema guiado passo-a-passo</h3>
              <p className="text-gray-300">Configuração completa em apenas 15 minutos</p>
            </div>
            
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-400 mb-3">❌ "Não tenho tempo para criar conteúdo todos os dias"</h3>
              <p className="text-gray-300">Sem conteúdo = sem vendas = sem resultado</p>
            </div>
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-400 mb-3">✅ IA gera conteúdo 24/7</h3>
              <p className="text-gray-300">Conteúdo profissional gerado automaticamente</p>
            </div>
            
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-400 mb-3">❌ "Demora muito para ver resultados"</h3>
              <p className="text-gray-300">Meses sem ganhar nada, desânimo, desistência</p>
            </div>
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-400 mb-3">✅ Primeiras vendas em 7-15 dias</h3>
              <p className="text-gray-300">Sistema otimizado para resultados rápidos</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              TECNOLOGIA DE PONTA
            </span>
            <br />
            <span className="text-2xl text-gray-300">QUE FAZ TUDO POR VOCÊ</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-6 text-center hover:bg-white/10 transition-all">
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revenue Potential */}
      <section className="py-20 bg-gradient-to-r from-green-900/20 to-emerald-900/20 backdrop-blur-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="text-green-400">POTENCIAL DE RECEITA COMPROVADO</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-lg border border-green-500/30 rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-green-400 mb-4">CONSERVADOR</h3>
              <div className="text-4xl font-bold text-white mb-2">R$ 15.400</div>
              <div className="text-gray-400 mb-6">por mês (Mês 3)</div>
              <ul className="text-left text-gray-300 space-y-2 text-sm">
                <li>✅ ClickBank: R$ 1.500</li>
                <li>✅ Hotmart: R$ 1.600</li>
                <li>✅ Monetizze: R$ 1.800</li>
                <li>✅ Amazon: R$ 4.000</li>
                <li>✅ Shopee: R$ 2.000</li>
                <li>✅ Magalu: R$ 1.500</li>
                <li>✅ Mercado Livre: R$ 2.000</li>
                <li>✅ Awin: R$ 1.000</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-lg border border-yellow-500/30 rounded-lg p-8 text-center transform scale-105">
              <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-bold mb-4 inline-block">
                MAIS POPULAR
              </div>
              <h3 className="text-2xl font-bold text-yellow-400 mb-4">OTIMISTA</h3>
              <div className="text-4xl font-bold text-white mb-2">R$ 40.600</div>
              <div className="text-gray-400 mb-6">por mês (Mês 6)</div>
              <ul className="text-left text-gray-300 space-y-2 text-sm">
                <li>✅ ClickBank: R$ 5.000</li>
                <li>✅ Hotmart: R$ 4.000</li>
                <li>✅ Monetizze: R$ 4.500</li>
                <li>✅ Amazon: R$ 9.600</li>
                <li>✅ Shopee: R$ 6.000</li>
                <li>✅ Magalu: R$ 4.500</li>
                <li>✅ Mercado Livre: R$ 5.000</li>
                <li>✅ Awin: R$ 2.000</li>
              </ul>
            </div>
            
            <div className="bg-white/5 backdrop-blur-lg border border-purple-500/30 rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-purple-400 mb-4">EXPERT</h3>
              <div className="text-4xl font-bold text-white mb-2">R$ 95.300</div>
              <div className="text-gray-400 mb-6">por mês (Mês 12)</div>
              <ul className="text-left text-gray-300 space-y-2 text-sm">
                <li>✅ ClickBank: R$ 15.000</li>
                <li>✅ Hotmart: R$ 9.600</li>
                <li>✅ Monetizze: R$ 10.800</li>
                <li>✅ Amazon: R$ 22.500</li>
                <li>✅ Shopee: R$ 12.000</li>
                <li>✅ Magalu: R$ 9.400</li>
                <li>✅ Mercado Livre: R$ 11.000</li>
                <li>✅ Awin: R$ 5.000</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            VEJA O QUE NOSSOS <span className="text-yellow-400">CLIENTES ESTÃO DIZENDO</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full mr-4" />
                  <div>
                    <h4 className="text-white font-bold">{testimonial.name}</h4>
                    <p className="text-gray-400 text-sm">{testimonial.location}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4">"{testimonial.text}"</p>
                <div className="border-t border-white/10 pt-4">
                  <div className="text-green-400 font-bold">{testimonial.earnings}</div>
                  <div className="text-gray-400 text-sm">em {testimonial.timeframe}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 backdrop-blur-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">
            🚨 <span className="text-red-400">OFERTA EXPIRA EM:</span>
          </h2>
          
          <div className="flex justify-center gap-4 mb-12">
            <div className="bg-black/50 rounded-lg p-4">
              <div className="text-3xl font-bold text-yellow-400">23</div>
              <div className="text-gray-400">Horas</div>
            </div>
            <div className="bg-black/50 rounded-lg p-4">
              <div className="text-3xl font-bold text-yellow-400">47</div>
              <div className="text-gray-400">Minutos</div>
            </div>
            <div className="bg-black/50 rounded-lg p-4">
              <div className="text-3xl font-bold text-yellow-400">32</div>
              <div className="text-gray-400">Segundos</div>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4">GARANTIA BLINDADA DE 30 DIAS</h3>
            <p className="text-gray-300">
              Se você não fizer pelo menos <strong className="text-green-400">R$ 2.000 nos primeiros 30 dias</strong>, 
              devolvemos 100% do seu investimento + R$ 500 pelo seu tempo!
            </p>
          </div>
          
          <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto mb-8">
            <div className="flex gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu melhor email"
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-6 py-3 rounded-lg font-bold hover:from-yellow-600 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {loading ? '...' : 'GARANTIR ACESSO'}
              </button>
            </div>
          </form>
          
          <button 
            onClick={handleStartNow}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-12 py-6 rounded-lg font-bold text-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-2xl"
          >
            💰 QUERO COMEÇAR A GANHAR R$ 15K+ POR MÊS →
          </button>
          
          <p className="text-yellow-400 font-bold mt-4">🚨 ÚLTIMAS 7 VAGAS DISPONÍVEIS 🚨</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-lg border-t border-white/10 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-2xl font-bold text-white mb-4">
            💰 <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">MonetizeMaster</span>
          </div>
          <p className="text-gray-400 mb-8">O Futuro do Marketing de Afiliados</p>
          
          <div className="flex justify-center space-x-8 mb-8">
            <a href="#" className="text-gray-400 hover:text-white transition-colors">Termos de Uso</a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors">Política de Privacidade</a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors">Suporte</a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors">Contato</a>
          </div>
          
          <p className="text-gray-500 text-sm">
            © 2024 MonetizeMaster. Todos os direitos reservados.
          </p>
        </div>
      </footer>
      
      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-lg z-50 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg p-6 max-w-4xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Demonstração do Sistema</h3>
              <button 
                onClick={() => setShowVideoModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="aspect-video bg-black/50 rounded-lg flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Play className="h-16 w-16 mx-auto mb-4" />
                <p>Vídeo de demonstração será carregado aqui</p>
                <p className="text-sm mt-2">Integre com YouTube, Vimeo ou servidor próprio</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPage;